import { PRODUCT_NAME, SITE_URL, TAGLINE } from "@mailhand/core/brand";
import { linkRequestSchema } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { useApi } from "../../app/services.js";
import { useTask } from "../../app/useTask.js";
import { Button, Eyebrow, Field, Input } from "../../ui/index.js";
import { AuthLayout } from "./AuthLayout.js";

export function LoginScreen() {
  const api = useApi();
  const task = useTask();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const parsed = linkRequestSchema.safeParse({ email: email.trim() });
    if (!parsed.success) {
      setError("Type a full email address, like sam@acmecoffee.com.");
      return;
    }
    setError(undefined);
    const ok = await task.run(() => api.call("authLink", { body: parsed.data }));
    if (ok) setSentTo(parsed.data.email);
  }

  if (sentTo !== null) {
    return (
      <AuthLayout>
        <Eyebrow live>Link sent</Eyebrow>
        <h1 className="headline">Check your email.</h1>
        <p className="auth-lede">We sent a sign-in link to {sentTo}. It works for 15 minutes.</p>
        <Button variant="ghost" onClick={() => { setSentTo(null); }}>
          Use a different address
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Eyebrow>Sign in</Eyebrow>
      <h1 className="headline">{TAGLINE}</h1>
      <p className="auth-lede">{PRODUCT_NAME} reads each email, picks the right action, and asks you when it is not sure.</p>
      <form className="auth-form" onSubmit={(event) => void submit(event)} noValidate>
        <Field label="Your email" error={error}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="you@business.com"
              value={email}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(event) => { setEmail(event.target.value); }}
              autoFocus
            />
          )}
        </Field>
        <Button type="submit" variant="primary" busy={task.busy}>
          Email me a sign-in link
        </Button>
      </form>
      <p className="auth-note">No password. The link signs you in on this device.</p>
      <p className="auth-note">
        By signing in you agree to the <a href={`${SITE_URL}/terms`}>Terms</a> and the <a href={`${SITE_URL}/privacy`}>Privacy Policy</a>.
      </p>
    </AuthLayout>
  );
}
